/**
 * OTP Report Export Utilities
 * Provides functionality to export OTP data using the backend report service
 */

import { format, parse } from 'date-fns';
import reports from '@/modules/Flexigo/services/reports';
import { track } from '@/modules/Flexigo/helpers/rum';

/**
 * Export OTP data as Excel report using the backend service
 * @param {Object} filters - Filters object from otpDashboard store
 * @param {Object} filters.dateRange - Date range object with fromDate/toDate or startDate/endDate
 * @param {Number} filters.threshold - Late arrival threshold in minutes
 * @param {Array} filters.routeIds - Array of route IDs to filter by
 * @param {Array} filters.driverIds - Array of driver IDs to filter by
 * @param {String} filters.direction - Direction filter (e.g., 'TO', 'FROM')
 * @param {Array} fallbackData - Optional fallback data array to extract dates from
 * @returns {Promise<void>}
 */
export async function exportOTPAsExcel(filters = null, fallbackData = null) {
  track('export:excel:otp-report');
  try {
    let startDate;
    let endDate;

    // Extract values from filters object
    const dateRange = filters?.dateRange;
    const threshold = filters?.threshold;

    // Extract dates from dateRange object
    if (dateRange) {
      // Handle fromDate/toDate format (YYYY-MM-DD string)
      if (dateRange.fromDate && dateRange.toDate) {
        startDate = parse(dateRange.fromDate, 'yyyy-MM-dd', new Date());
        endDate = parse(dateRange.toDate, 'yyyy-MM-dd', new Date());
      }
      // Handle startDate/endDate format (Date objects or strings)
      else if (dateRange.startDate && dateRange.endDate) {
        startDate = dateRange.startDate instanceof Date ? dateRange.startDate : new Date(dateRange.startDate);
        endDate = dateRange.endDate instanceof Date ? dateRange.endDate : new Date(dateRange.endDate);
      }
    }

    // Fallback: use first and last dates from data array
    if ((!startDate || !endDate) && fallbackData && fallbackData.length > 0) {
      // Check if fallbackData has valid date properties
      if (fallbackData[0]?.date && fallbackData[fallbackData.length - 1]?.date) {
        const firstDate = fallbackData[0].date;
        const lastDate = fallbackData[fallbackData.length - 1].date;

        // Handle YYYYMMDD integer format (e.g., 20240101)
        if (typeof firstDate === 'number' || (typeof firstDate === 'string' && /^\d{8}$/.test(firstDate))) {
          const dateStr = String(firstDate);
          startDate = parse(dateStr, 'yyyyMMdd', new Date());
        } else {
          startDate = new Date(firstDate);
        }

        if (typeof lastDate === 'number' || (typeof lastDate === 'string' && /^\d{8}$/.test(lastDate))) {
          const dateStr = String(lastDate);
          endDate = parse(dateStr, 'yyyyMMdd', new Date());
        } else {
          endDate = new Date(lastDate);
        }
      }
    }

    // Validate we have dates
    if (!startDate || !endDate) {
      console.error('No date range available for OTP report export');
      return;
    }

    // Validate dates are valid
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      console.error('Invalid date range for OTP report export:', { startDate, endDate });
      return;
    }

    // Format dates as dd.MM.yyyy (backend expects this format)
    const params = {
      startDay: format(startDate, 'dd.MM.yyyy'),
      endDay: format(endDate, 'dd.MM.yyyy'),
      threshold: threshold || 5,
    };

    // Add optional filter parameters if available
    if (filters) {
      // Route IDs: extract IDs from objects if needed, convert array to comma-separated string
      if (filters.routeIds && Array.isArray(filters.routeIds) && filters.routeIds.length > 0) {
        const routeIdValues = filters.routeIds.map((r) => (typeof r === 'object' ? r.id : r));
        params.routeIds = routeIdValues.join(',');
      }

      // Driver IDs: extract IDs from objects if needed, convert array to comma-separated string
      if (filters.driverIds && Array.isArray(filters.driverIds) && filters.driverIds.length > 0) {
        const driverIdValues = filters.driverIds.map((d) => (typeof d === 'object' ? d.id : d));
        params.driverIds = driverIdValues.join(',');
      }

      // Direction: add if provided and not 'all', convert to uppercase
      if (filters.direction && filters.direction !== 'all') {
        params.direction = filters.direction.toUpperCase();
      }
    }

    // Call the OTP report endpoint
    const reportResponse = await reports.getReportByType('/report/otp-report', params);

    if (reportResponse?.response?.fileId) {
      const { fileId } = reportResponse.response;
      const BASEURL = process.env.NODE_ENV === 'development' ? '/flexigo' : '/commute';
      const path = window.location.origin.includes('localhost')
        ? 'http://localhost:5431'
        : `${window.location.origin}${BASEURL}`;
      const url = `${path}/doc/report/${fileId}`.replace('/index.html', '');
      window.open(url, '_blank');
    } else {
      console.error('No report file generated from OTP report service');
    }
  } catch (error) {
    console.error('Error exporting OTP report:', error);
    throw error;
  }
}

/**
 * Export OTP data as Excel with additional error handling and user feedback
 * @param {Object} options - Export options
 * @param {Object} options.filters - Filters object from otpDashboard store
 * @param {Array} options.fallbackData - Fallback data array
 * @param {Function} options.onSuccess - Success callback
 * @param {Function} options.onError - Error callback
 * @returns {Promise<void>}
 */
export async function exportOTPAsExcelWithFeedback({
  filters = null,
  fallbackData = null,
  onSuccess = null,
  onError = null,
}) {
  try {
    await exportOTPAsExcel(filters, fallbackData);
    if (onSuccess) {
      onSuccess();
    }
  } catch (error) {
    if (onError) {
      onError(error);
    } else {
      console.error('Failed to export OTP report:', error);
    }
  }
}
